// MobilePodcast — the weekly "Capital and Context Podcast" episode list + player.
//
// BASE (main / m27, PR #30): consumption-only surface. Episodes are synced daily from the
// public YouTube playlist by the `podcast-sync` edge function into `podcast_episodes`
// (video_id text PK), fetched here via the `mobile_podcast_episodes()` SECURITY DEFINER RPC
// (matching the established mobile fetch convention — see MobileOutputs/MobileHome).
//
// EXTENSION (podcast publish pipeline, this branch, reconciled 2026-07-04): `mobile_podcast_episodes()`
// (see docs/build-records/sql/m28_podcast_publish.sql §5) now LEFT JOINs the publish-side
// `podcast_publish_episodes` table on `youtube_video_id = video_id`, so a synced episode that
// was ALSO published through the admin upload pipeline carries `audio_path`/`audio_bytes`/
// `audio_mime`/`publish_episode_id` alongside the original m27 columns. When `audio_path` is
// present we prefer NATIVE AUDIO playback (`usePodcastAudio` — background-audio capable,
// offline predownload, lock-screen transport) over the YouTube iframe; otherwise this falls
// back to the original m27 iframe-only experience unchanged.
//
// Offline-resilient exactly like the sibling mobile screens: useCachedQuery write-throughs the
// resolved array (queryFn THROWS on { error } so the last-known-good cache keeps serving instead
// of being overwritten with an empty/partial result), and the "Saved <relative time>" amber stale
// hint is copied verbatim from MobileOutputs/MobileHome.
//
// Route: /partner/m/podcast (single screen — list + player, no separate detail route for v1).
// Entry point: a NavCard in MobileHome.tsx's "Explore" section.

import { useEffect, useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { Link } from 'react-router-dom';
import { ChevronLeft, Headphones, Pause, Play, WifiOff } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { usePodcastAudio } from '@/hooks/usePodcastAudio';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_podcast_episodes() builds (m27 columns + the publish-
// pipeline native-audio extension columns, all nullable so the m27-only shape still parses).
// ---------------------------------------------------------------------------

interface EpisodeRow {
  video_id: string;
  title: string;
  description: string | null;
  published_at: string;
  thumbnail_url: string | null;
  duration_sec: number | null;
  // Native-audio preference (podcast publish pipeline) — null when this synced episode was
  // never (or not yet) published through the upload pipeline.
  publish_episode_id: string | null;
  audio_path: string | null;
  audio_bytes: number | null;
  audio_mime: string | null;
}

const EMPTY_EPISODES: EpisodeRow[] = [];

async function fetchEpisodes(): Promise<EpisodeRow[]> {
  const { data, error } = await (supabase.rpc as any)('mobile_podcast_episodes');
  // MUST throw so useCachedQuery keeps the cached placeholder offline instead of overwriting it.
  if (error) throw error;
  return Array.isArray(data) ? (data as EpisodeRow[]) : [];
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

function formatPublished(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return format(new Date(iso), 'EEE d MMM yyyy');
  } catch {
    return null;
  }
}

function fmtDuration(sec: number | null): string | null {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Seconds → m:ss (or h:mm:ss for long episodes) — used by the native-audio player's transport.
function fmtTime(total: number): string {
  if (!Number.isFinite(total) || total < 0) total = 0;
  const s = Math.floor(total % 60);
  const m = Math.floor((total / 60) % 60);
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

// ---------------------------------------------------------------------------
// Native-audio player card — only rendered when the selected episode has an audio_path (i.e.
// it was published through the upload pipeline). Owns its own usePodcastAudio() instance so
// it only resolves/loads audio for episodes that actually have some (never for pure
// YouTube-sync-only episodes, which stay on the iframe below).
// ---------------------------------------------------------------------------

function NativeAudioPlayer({ selected }: { selected: EpisodeRow }) {
  const podcast = usePodcastAudio();
  // Separate from the hook's own resolveFailed flag: this flips true when the <audio> element
  // itself errors out (e.g. the CDN fetch fails) even though audio_path existed, so we still
  // fall back to the YouTube iframe rather than showing a dead player.
  const [playbackErrored, setPlaybackErrored] = useState(false);

  const {
    selectEpisode,
    episode,
    useYoutubeFallback,
    playing,
    currentTime,
    duration,
    rate,
    downloaded,
    error,
    toggle,
    seek,
    setRate,
  } = podcast;

  // Keep usePodcastAudio's internal selection in sync with the screen's selected episode (it
  // fetches its own episode list keyed by publish_episode_id — see usePodcastAudio.ts).
  useEffect(() => {
    if (selected.publish_episode_id) selectEpisode(selected.publish_episode_id);
  }, [selected.publish_episode_id, selectEpisode]);

  useEffect(() => {
    setPlaybackErrored(false);
  }, [selected.publish_episode_id]);

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const showYoutubeIframe = (useYoutubeFallback || (!!error && playbackErrored)) && !!selected.video_id;

  if (showYoutubeIframe) {
    return <YoutubeIframe videoId={selected.video_id} title={selected.title} />;
  }

  // Still resolving this episode's audio (hook hasn't matched it up yet) — show the iframe as a
  // safe default rather than a blank player.
  if (!episode || episode.id !== selected.publish_episode_id) {
    return <YoutubeIframe videoId={selected.video_id} title={selected.title} />;
  }

  return (
    <div className="mt-3">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? 'Pause episode' : 'Play episode'}
          aria-pressed={playing}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow active:opacity-90"
        >
          {playing ? (
            <Pause className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Play className="ml-0.5 h-6 w-6" aria-hidden="true" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <input
            type="range"
            min={0}
            max={Math.max(1, Math.floor(duration))}
            step={1}
            value={Math.min(currentTime, duration || currentTime)}
            onChange={(e) => seek(Number(e.target.value))}
            aria-label="Seek within the episode"
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
            style={{
              background: `linear-gradient(to right, hsl(var(--primary)) ${pct}%, hsl(var(--muted)) ${pct}%)`,
            }}
          />
          <div className="mt-1 flex items-center justify-between font-mono text-[11px] tabular-nums text-muted-foreground">
            <span>{fmtTime(currentTime)}</span>
            <span>{fmtTime(duration)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setRate(rate === 1 ? 1.5 : 1)}
          aria-label={`Playback speed ${rate === 1 ? '1x' : '1.5x'}, tap to change`}
          className="shrink-0 rounded-full border border-border px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground active:bg-muted/50"
        >
          {rate === 1 ? '1×' : '1.5×'}
        </button>
      </div>

      {error && (
        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-[11px] uppercase tracking-wide text-amber-600">{error}</p>
          <button
            type="button"
            onClick={() => setPlaybackErrored(true)}
            className="shrink-0 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground active:bg-muted/50"
          >
            Watch on YouTube instead
          </button>
        </div>
      )}
      {downloaded && !error && (
        <p className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground/70">
          Saved for offline listening
        </p>
      )}
    </div>
  );
}

function YoutubeIframe({ videoId, title }: { videoId: string; title: string }) {
  const online = useIsOnline();
  if (!online) {
    return (
      <div className="mt-3 flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg bg-muted/40 p-4 text-center">
        <WifiOff className="h-6 w-6 text-amber-600" aria-hidden="true" />
        <p className="text-[11px] uppercase tracking-wide text-amber-600">
          You’re offline — playback needs a connection.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3 aspect-video w-full overflow-hidden rounded-lg bg-black">
      <iframe
        key={videoId}
        src={`https://www.youtube.com/embed/${videoId}?playsinline=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="h-full w-full border-0"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobilePodcast() {
  const online = useIsOnline();

  const list = useCachedQuery<EpisodeRow[]>({
    queryKey: ['mobile-podcast-episodes'],
    cacheKey: 'mobile.podcast_episodes',
    queryFn: fetchEpisodes,
  });

  const items = list.data ?? EMPTY_EPISODES;

  // Selected episode for the inline player. No autoplay on cold load (P0-quiet-default) — the
  // player section only appears once an episode has actually been selected by an explicit tap.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId ? items.find((e) => e.video_id === selectedId) ?? null : null;

  const showStale = list.isFromCache && list.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && list.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(list.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const hardError = list.isError && !list.isFromCache && list.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <h1 className="font-heading text-2xl font-semibold text-primary">Podcast</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {items.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {items.length} episode{items.length === 1 ? '' : 's'}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load the podcast list just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved episode list yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : items.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Headphones className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">No episodes yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Episodes sync daily.</p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        ) : (
          <>
            {/* Inline player — only shown once an episode has been explicitly tapped. Prefers
                native audio (usePodcastAudio) when this episode was published through the
                upload pipeline (audio_path present); otherwise the YouTube iframe, unchanged
                from the original m27 experience. */}
            {selected && (
              <section className="overflow-hidden rounded-xl border border-border bg-card p-4">
                {selected.audio_path ? (
                  <NativeAudioPlayer selected={selected} />
                ) : (
                  <YoutubeIframe videoId={selected.video_id} title={selected.title} />
                )}
                <div className="mt-4">
                  <h2 className="font-heading text-sm font-semibold leading-snug">{selected.title}</h2>
                  {formatPublished(selected.published_at) && (
                    <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                      {formatPublished(selected.published_at)}
                    </p>
                  )}
                  {selected.description && (
                    <p className="mt-2 line-clamp-4 text-sm text-muted-foreground">
                      {selected.description}
                    </p>
                  )}
                </div>
              </section>
            )}

            {/* Episode menu — newest first (RPC already orders published_at desc). */}
            <ul className="space-y-2">
              {items.map((ep) => {
                const active = ep.video_id === selectedId;
                const when = formatPublished(ep.published_at) ?? formatRelative(ep.published_at);
                const duration = fmtDuration(ep.duration_sec);
                return (
                  <li key={ep.video_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(ep.video_id)}
                      aria-current={active ? 'true' : undefined}
                      aria-label={`Play ${ep.title}`}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors',
                        active
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-card active:bg-muted/50',
                      )}
                    >
                      <span className="relative h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-muted">
                        {ep.thumbnail_url && (
                          <img
                            src={ep.thumbnail_url}
                            alt=""
                            aria-hidden="true"
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        )}
                        <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Play className="h-5 w-5 fill-white text-white" aria-hidden="true" />
                        </span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block font-heading text-sm font-semibold leading-snug line-clamp-2',
                            active && 'text-primary',
                          )}
                        >
                          {ep.title}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                          {when && <span>{when}</span>}
                          {duration && <span>{duration}</span>}
                          {ep.audio_path && <span>Native audio</span>}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
